import React from 'react';
import { Link } from 'react-router-dom';

import GridContainer from '@jumbo/components/GridContainer';
import CmtCardContent from '@coremat/CmtCard/CmtCardContent';
import CmtCardHeader from '@coremat/CmtCard/CmtCardHeader';
import CmtCard from '@coremat/CmtCard';
import { Box, Button, Fab, Grid, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { usePreviousProjets } from 'services/Data/ProjectServices';
import useTranslateEnum from 'services/Hooks/useTranslateEnum';
import ProjectStatusEnum, { ProjectStatusEnumValue } from 'enums/ProjectStatusEnum';
import ThemedTooltip from '@acnb/CustomComponents/Tooltip/ThemedTooltip';
import { CalendarMonth, Face, LocalShipping, Visibility } from '@mui/icons-material';
import useWidth from 'services/Hooks/useWidth';
import CmtGridView from '@coremat/CmtGridView';
import CmtCardFooter from '@coremat/CmtCard/CmtCardFooter';

const ProfilePreviousProjects = () => {
  const { t } = useTranslation();
  const { translateEnum } = useTranslateEnum();
  const theme = useTheme();
  const width = useWidth();

  const { data: previousProjects } = usePreviousProjets();

  return (
    <Box mb={6}>
      <CmtCard>
        <CmtCardHeader
          style={{ minHeight: '60px' }}
          separator={{
            color: theme.palette.borderColor.dark,
          }}
          title={t('previousProjects')}></CmtCardHeader>
        <CmtCardContent>
          {width === 'xs' ? (
            previousProjects?.map(proj => (
              <CmtCard key={proj.id}>
                <CmtCardHeader
                  separator={{
                    color: theme.palette.borderColor.dark,
                  }}
                  title={proj.projectNumber}
                  subTitle={proj.contactName}
                />
                <CmtCardContent>
                  <CmtGridView
                    itemPadding={24}
                    responsive={{
                      xs: 1,
                      sm: 2,
                      md: 3,
                      lg: 3,
                      xl: 3,
                    }}
                    data={[
                      {
                        title: t('date'),
                        desc: proj.projectStatusHistories?.find(
                          history => history.newStatus === ProjectStatusEnumValue.PendingApproval,
                        )?.dateStatusChanged
                          ? moment(
                              proj.projectStatusHistories?.find(
                                history => history.newStatus === ProjectStatusEnumValue.PendingApproval,
                              )?.dateStatusChanged,
                            ).format(t('date_format'))
                          : '',
                        icon: <CalendarMonth />,
                      },
                      {
                        title: t('targetedShippingDate'),
                        desc: proj.targetedShippingDate ? moment(proj.targetedShippingDate).format(t('date_format')) : '-',
                        icon: <LocalShipping />,
                      },
                      { title: t('brpContact'), desc: proj.userFullName, icon: <Face /> },
                      { title: t('status'), desc: translateEnum(ProjectStatusEnum, proj.status) },
                    ]}
                    renderRow={(item, index) => (
                      <Box display="flex" alignItems="center" key={index}>
                        {item.icon}
                        <Box ml={3}>
                          <Box fontSize={12} color="text.secondary">
                            {item.title}
                          </Box>
                          <Box>{item.desc ? item.desc : item.list}</Box>
                        </Box>
                      </Box>
                    )}
                  />
                </CmtCardContent>
                <CmtCardFooter>
                  <Grid item xs={12} textAlign="center">
                    <Link to={`/dealer/profile/previous-projects/${proj.id}`}>
                      <Button variant="outlined">{t('see_project')}</Button>
                    </Link>
                  </Grid>
                </CmtCardFooter>
              </CmtCard>
            ))
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('date')}</TableCell>
                  <TableCell>{t('targetedShippingDate')}</TableCell>
                  <TableCell>{t('dealerContact')}</TableCell>
                  <TableCell>{t('brpContact')}</TableCell>
                  <TableCell>{t('status')}</TableCell>
                  <TableCell>{t('actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {previousProjects?.map(proj => (
                  <TableRow key={proj.id}>
                    <TableCell>
                      {proj.projectStatusHistories?.find(
                        history => history.newStatus === ProjectStatusEnumValue.PendingApproval,
                      )?.dateStatusChanged
                        ? moment(
                            proj.projectStatusHistories?.find(
                              history => history.newStatus === ProjectStatusEnumValue.PendingApproval,
                            )?.dateStatusChanged,
                          ).format(t('date_format'))
                        : ''}
                    </TableCell>
                    <TableCell>
                      {proj.targetedShippingDate ? moment(proj.targetedShippingDate).format(t('date_format')) : '-'}
                    </TableCell>
                    <TableCell>{proj.contactName}</TableCell>
                    <TableCell>{proj.userFullName}</TableCell>
                    <TableCell>{translateEnum(ProjectStatusEnum, proj.status)}</TableCell>
                    <TableCell>
                      <ThemedTooltip title={t('see_project')} placement={'top'}>
                        <Link to={`/dealer/profile/previous-projects/${proj.id}`}>
                          <Fab
                            color="primary"
                            sx={{
                              height: 26,
                              width: 26,
                              minHeight: 26,
                              '& svg': {
                                height: 14,
                                width: 14,
                              },
                            }}>
                            <Visibility />
                          </Fab>
                        </Link>
                      </ThemedTooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <GridContainer spacing={0} className="mt-5"></GridContainer>
        </CmtCardContent>
      </CmtCard>
    </Box>
  );
};

export default ProfilePreviousProjects;
